import { gql, useFragment } from "@apollo/client";
import { useState } from "react";

// Lightbox
import FsLightbox from "fslightbox-react";
// Slideshow
import { SplideSlide } from "@splidejs/react-splide";
import "@splidejs/react-splide/css";

// Style
import style from "./gallery.module.scss";

// Constants
import { galleryBlock } from "@/constants/constants";
import { slideSet } from "@/constants/gql-fields";

// Components
import Container from "@/components-layout/container/container";
import ModuleSettings from "@/components-modules/module-settings/module-settings";
import ModuleImage from "@/components-modules/module-image/module-image";
import Slideshow from "@/components/slideshow/slideshow";

export default function Gallery({ module }) {
  // - Fragment
  const { complete, data } = useFragment({
    fragment: Gallery.fragments.module,
    fragmentName: "GalleryFragment",
    from: {
      __typename: module.__typename,
      id: module.id,
    },
  });

  if (!complete) {
    return;
  }

  // - Variables
  const moduleSettings = data.galleryBlockModuleSettings;
  const galleryItems = data.gallery.nodes;
  const gallerySettings = data.gallerySettings;

  // Lightbox
  const isLightbox = gallerySettings?.includes("lightbox") ?? false;
  const [toggler, setToggler] = useState(false);
  let lightboxSources = [];
  galleryItems.forEach((item) => {
    lightboxSources.push(item.sourceUrl);
  });

  // Slideshow
  const isSlideshow = gallerySettings?.includes("slideshow") ?? false;
  const slideshowSettings = data.carouselSettings;

  return (
    <section
      id={moduleSettings.module_id}
      className={`${moduleSettings.classes ? moduleSettings.classes : ""} ${
        style.galleryModule
      }`}
      lightbox={isLightbox ? "true" : "false"}
      preset={data.presetModule && data.presetModule[0]}
    >
      <ModuleSettings settings={moduleSettings} />

      <Container>
        {isSlideshow ? ( // Slideshow
          <Slideshow settings={slideshowSettings}>
            {galleryItems.map((item, index) => {
              let image = {};
              image["node"] = item;
              return (
                <SplideSlide
                  slide={index}
                  onClick={(e) => {
                    if (isLightbox) {
                      setToggler(!toggler);
                    }
                  }}
                >
                  <ModuleImage image={image} />
                </SplideSlide>
              );
            })}
          </Slideshow>
        ) : (
          // Not Slideshow
          <div
            className={`gallery ${style.items}`}
            data-grid-items={data.galleryColumns}
          >
            {galleryItems.map((item, index) => {
              let image = {};
              image["node"] = item;
              return (
                <div
                  className="item"
                  key={index}
                  slide={index}
                  onClick={(e) => {
                    if (isLightbox) {
                      setToggler(!toggler);
                    }
                  }}
                >
                  <ModuleImage image={image} />
                </div>
              );
            })}
          </div>
        )}

        {isLightbox && (
          <FsLightbox
            toggler={toggler}
            sources={lightboxSources}
            exitFullscreenOnClose={true}
            slide="3"
            onOpen={(e) => {
              e.elements.container.current
                .closest(".container")
                ?.setAttribute("style", "z-index: 2;");
            }}
            onClose={(e) => {
              e.elements.container.current
                .closest(".container")
                ?.setAttribute("style", "");
            }}
          />
        )}
      </Container>
    </section>
  );
}

Gallery.fragments = {
  module: gql`
    fragment GalleryFragment on ${galleryBlock} {
      id
      __typename
      presetModule
      gallery {
        nodes {
          id
          altText
          sourceUrl
          caption
        }
      }
      gallerySettings
      galleryColumns
      uniqueId
      ${slideSet}
      # -- Module General Settings
      galleryBlockModuleSettings {
        ...ModSetFragment @unmask(mode: "migrate")
      }
    }
    ${ModuleSettings.fragments.entry}
  `,
};
